import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DocumentGenericField } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GenericItemTypeEnum } from 'src/app/domain/enums/generic-item-type.enum';
import { RaToastService } from '@shared/components';
import { RaSwitchComponent } from 'ra-ui';
import { GenericTextFieldPartial } from 'src/app/domain/entities/partials/generics/generic-text-field.partial';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import {
    DATA_TAG_CONTEXT,
    DataTagContext,
} from 'src/app/shared/components/ra-editor/data-tag-context.token';
import { getDataTagLabel } from 'src/app/shared/components/ra-editor/data-tag-i18n';
import { resolveDataTagValue } from 'src/app/shared/components/ra-editor/data-tag-value.resolver';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { sanitizeGenericFields } from 'src/app/shared/utils/generic-fields.util';
import {
    LinkTokenComposerValue,
    RaLinkTokenComposerComponent,
} from 'src/app/shared/components/ra-link-token-composer/ra-link-token-composer.component';

export interface FooterEditorSaveEvent {
    footer: DocumentGenericField[];
}

/** Ícone por `fieldId` (mirror do mobile, limitado aos ícones disponíveis na web). */
const FIELD_ICONS: Record<string, string> = {
    cellphone: 'mobile',
    phone: 'mobile',
    instagram: 'camera',
    site: 'global',
    email: 'email',
    company_email: 'email',
};

@Component({
    selector: 'modal-footer-editor',
    templateUrl: 'modal-footer-editor.component.html',
    styleUrl: 'modal-footer-editor.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslatePipe,
        RaModalComponent,
        RaButtonComponent,
        RaSwitchComponent,
        RaIconComponent,
        RaLinkTokenComposerComponent,
    ],
})
export class ModalFooterEditorComponent {
    private _translate = inject(TranslateService);
    private _toast = inject(RaToastService);
    private _dataTagContext = inject(DATA_TAG_CONTEXT, { optional: true });

    public footer = input.required<DocumentGenericField[]>();
    public loading = input<boolean>(false);

    public close = output<void>();
    public save = output<FooterEditorSaveEvent>();

    public readonly MAX_VISIBLE = 4;

    /** Cópia de trabalho local, semeada uma vez na abertura (evita clobber do realtime). */
    public items = signal<DocumentGenericField[]>([]);
    public visibilityControls = new Map<string, FormControl<boolean>>();

    private _seeded = false;

    public editableItems = computed(() =>
        this.items().filter(i => i.canEdit && i.type === GenericItemTypeEnum.TEXT)
    );

    /** Conta os itens editáveis visíveis — é o que o usuário vê como toggle e o que o badge mostra. */
    public visibleCount = computed(() => this.editableItems().filter(i => i.isVisible).length);

    /** Limite atingido: usado para desabilitar os toggles desligados. */
    public isAtMax = computed(() => this.visibleCount() >= this.MAX_VISIBLE);

    public canAct = computed(() => this.visibleCount() > 0 && !this.loading());

    /** Linhas resolvidas para o preview ilustrativo. */
    public previewLines = computed(() => {
        const ctx = this._dataTagContext?.() ?? null;
        return this.items()
            .filter(i => i.isVisible && i.type === GenericItemTypeEnum.TEXT)
            .map(i => ({
                icon: this.iconFor(i),
                text: this._resolveDisplay((i.value ?? {}) as GenericTextFieldPartial, ctx),
            }))
            .filter(line => (line.text ?? '').trim().length > 0);
    });

    constructor() {
        effect(() => {
            const footer = this.footer();
            if (this._seeded) return;
            this._seeded = true;
            untracked(() => this._seed(footer));
        });

        // No limite, desabilita os toggles desligados (impede ativar um 5º item).
        // `control.disable/enable` é o jeito reativo-forms (sem warning de `[disabled]`)
        // e reflete no ra-switch quando o CD roda — disparado por esta mesma mudança de `items`.
        effect(() => {
            const atMax = this.isAtMax();
            const list = this.items();
            untracked(() => {
                for (const it of list) {
                    const c = it.fieldId ? this.visibilityControls.get(it.fieldId) : undefined;
                    if (!c) continue;
                    const shouldDisable = atMax && !it.isVisible;
                    if (shouldDisable && !c.disabled) c.disable({ emitEvent: false });
                    else if (!shouldDisable && c.disabled) c.enable({ emitEvent: false });
                }
            });
        });
    }

    private _seed(footer: DocumentGenericField[]): void {
        // Clona raso item + value para não mutar o documento original. O spread
        // transformaria um item fora de forma (ex.: `[]` vindo do banco) num `{}`, que
        // seria devolvido no save — por isso filtra antes. Ver [sanitizeGenericFields].
        const clone: DocumentGenericField[] = sanitizeGenericFields(footer).map(item => ({
            ...item,
            value: item.value ? { ...item.value } : item.value,
        }));
        this.items.set(clone);

        this.visibilityControls.clear();
        for (const item of clone) {
            if (!item.fieldId) continue;
            const fieldId = item.fieldId;
            const ctrl = new FormControl<boolean>(!!item.isVisible, { nonNullable: true });
            ctrl.valueChanges.subscribe(val => this._onToggle(fieldId, val));
            this.visibilityControls.set(fieldId, ctrl);
        }
    }

    public controlFor(fieldId?: string): FormControl<boolean> | null {
        return (fieldId && this.visibilityControls.get(fieldId)) || null;
    }

    /**
     * Clique no card de um item: se o limite foi atingido e o item está desligado
     * (toggle desabilitado), avisa que só dá pra escolher até MAX_VISIBLE.
     */
    public onCardHeadClick(item: DocumentGenericField): void {
        if (this.isAtMax() && !item.isVisible) {
            this._toast.warning({
                message: this._translate.instant('document.edit.footerEditor.maxItems', {
                    max: this.MAX_VISIBLE,
                }),
            });
        }
    }

    private _onToggle(fieldId: string, val: boolean): void {
        // O limite é garantido desabilitando os toggles desligados no effect; aqui só
        // reflete a escolha no estado de trabalho.
        this.items.update(list =>
            list.map(i => (i.fieldId === fieldId ? { ...i, isVisible: val } : i))
        );
    }

    public onComposerChange(fieldId: string | undefined, value: LinkTokenComposerValue): void {
        if (!fieldId) return;
        this.items.update(list =>
            list.map(item => {
                if (item.fieldId !== fieldId) return item;
                const v: GenericTextFieldPartial = { ...(item.value as GenericTextFieldPartial) };
                if (value.link) {
                    v.text = value.link.value || this._tokenFromTagName(value.link.tagName);
                    v.linkTagName = value.link.tagName;
                    v.linkValueConverted = undefined;
                } else {
                    v.text = value.text ?? '';
                    v.linkTagName = undefined;
                    v.linkValueConverted = undefined;
                }
                return { ...item, value: v };
            })
        );
    }

    public iconFor(item: DocumentGenericField): string {
        return (item.fieldId && FIELD_ICONS[item.fieldId]) || 'global';
    }

    public titleFor(item: DocumentGenericField): string {
        return this._translate.instant(`document.edit.footerEditor.fields.${item.fieldId}.title`);
    }

    public descFor(item: DocumentGenericField): string {
        return this._translate.instant(
            `document.edit.footerEditor.fields.${item.fieldId}.description`
        );
    }

    public textValue(item: DocumentGenericField): string {
        return (item.value as GenericTextFieldPartial)?.text ?? '';
    }

    public linkTagNameOf(item: DocumentGenericField): string {
        return (item.value as GenericTextFieldPartial)?.linkTagName ?? '';
    }

    public limitCharOf(item: DocumentGenericField): number | undefined {
        return (item.value as GenericTextFieldPartial)?.limitChar;
    }

    /** Ref ao modal principal para disparar a animação de saída programaticamente. */
    public mainModal = viewChild<RaModalComponent>('mainModal');

    /**
     * Fecha o editor com animação (saída pela direita). O desmonte real acontece
     * no `(close)` do modal principal.
     */
    public animateClose(): void {
        this.mainModal()?.handleClose();
    }

    public confirmSave(): void {
        // Grava no documento; o alcance (padrão dos próximos, todos do serviço) é
        // escolhido depois, no botão Salvar das definições.
        this.save.emit({ footer: this.items() });
    }

    private _tokenFromTagName(tagName: string): string {
        return '$' + tagName.replace(/^link_/, '').toUpperCase();
    }

    private _resolveDisplay(v: GenericTextFieldPartial, ctx: DataTagContext | null): string {
        if (v.linkValueConverted) return v.linkValueConverted;

        const isToken = !!v.linkTagName || (v.text ?? '').startsWith('$');
        if (isToken) {
            const tag = v.linkTagName || v.text || '';
            const resolved = resolveDataTagValue(tag, ctx);
            if (resolved != null && String(resolved).trim() !== '') return String(resolved);
            // Sem valor real → rótulo do token (preview é apenas ilustrativo).
            return getDataTagLabel(this._translate, tag);
        }
        return v.text ?? '';
    }
}
